/**
 * OVERVOLT — Puzzle Mode level data (Sprint 30 / puzzle-impl-s30).
 *
 * Implements the spec in `docs/PUZZLE_DESIGN.md` §1 and §7. Every level is
 * **pure data** in TypeScript — the simulation is identical to Campaign /
 * Endless / Maze, but the path, the buildable tile set, the gold budget, the
 * allowed towers and the wave are all driven from the loaded level's record
 * rather than from the live `map.ts` bindings + Overload tier.
 *
 * The module has one side-effect at import time: every authored puzzle level
 * registers a synthetic entry in the {@link MAPS} registry (a key per level,
 * keyed `puzzle:<level.id>`) so the simulation's existing path / blocked-tile
 * pipeline (`isPathCell` / `isBlockedTile` / `recomputePaths` / `placement
 * Reject`) picks up the puzzle's geometry without `map.ts` itself needing to
 * change. Wall tiles (both inert `blockedTiles` and impenetrable `losWalls`)
 * collapse into the map's `blocked` set — placement / pathing reject both;
 * the LOS filter discriminates them by `losWalls` alone (see `los.ts`).
 *
 * Sprint 30 ships **6 hand-authored levels** (P0–P5) covering the spec's
 * five tower roles plus an LOS-wall sampler. P6 / P7 (boss + graduation) are
 * follow-on stretch — out of scope this sprint. See PUZZLE_DESIGN.md §7.
 */
import type { BossAddSpec, TowerType, WaveDef } from '../config';
import { MAPS, type Cell } from './map';
import { packLosWalls } from './los';

/**
 * A pure tower placement in a puzzle attempt — `(type, col, row)` plus an
 * optional upgrade target. `level: 1` means "place; do not upgrade"; `level:
 * 2` / `3` means "place, then upgrade once / twice from L1". Field order is
 * the order the evaluator applies operations.
 */
export interface PuzzlePlacement {
  readonly type: TowerType;
  readonly col: number;
  readonly row: number;
  /** Target tower level after upgrades. 1 by default. */
  readonly level?: 1 | 2 | 3;
}

/**
 * A scripted Overclock toggle applied by the evaluator at a specific tick
 * during the wave. Each entry maps to a `setOverclock(state, towerId, on)`
 * call on the tower whose (col, row) matches. Per `docs/PUZZLE_DESIGN.md` §7
 * (P8) + `docs/REC3_DESIGN.md` §7.6 — the puzzle's intended solution may
 * include the in-wave Overclock burst window, which a static layout cannot
 * express on its own. Field order is application order; the evaluator
 * processes the schedule entries whose `atTick` matches the current
 * tick AFTER `simTick` runs, so the toggle takes effect on the next tick.
 *
 * Determinism-safe: pure integer ticks; no RNG. A schedule entry referring
 * to a tile without a tower is a quiet no-op.
 */
export interface PuzzleOverclockEvent {
  readonly atTick: number;
  readonly col: number;
  readonly row: number;
  readonly on: boolean;
}

/**
 * Optional per-level overrides for the boss-block constants in `CONFIG`
 * (P7 / Kernel-Echo only — see `docs/PUZZLE_DESIGN.md` §1.2 and
 * `docs/KERNEL_REWORK_DESIGN.md` §4.4). A puzzle that contains a Kernel
 * locks the boss's mechanical spec via these overrides so the level remains
 * solvable even if a future sprint retunes the live (Campaign / Endless)
 * boss constants. The evaluator snapshots the named `CONFIG` fields, writes
 * the override values for the duration of the attempt, and restores them
 * after — single-threaded puzzle sim, determinism-safe.
 *
 * Every field is optional; missing fields fall through to the live
 * `CONFIG` value. The `bossWave` field is the puzzle-local override of
 * `state.wave` (which `createBoss` reads for the HP curve) — defaults to
 * the puzzle's own `state.wave = 0` if unset.
 */
export interface PuzzleBossDifficulty {
  /** Override for the boss's `state.wave` HP-scaling input. */
  readonly bossWave?: number;
  /** Override `CONFIG.bossBaseHp`. */
  readonly bossBaseHp?: number;
  /** Override `CONFIG.bossPhase2HpFrac`. */
  readonly bossPhase2HpFrac?: number;
  /** Override `CONFIG.bossPhase3HpFrac`. */
  readonly bossPhase3HpFrac?: number;
  /** Override `CONFIG.bossPhase2Adds`. */
  readonly bossPhase2Adds?: BossAddSpec;
  /** Override `CONFIG.bossPhase3Adds`. */
  readonly bossPhase3Adds?: BossAddSpec;
  /** Override `CONFIG.bossDrainInterval` (phase 1 / 2 / 3, in ticks). */
  readonly bossDrainInterval?: readonly [number, number, number];
  /** Override `CONFIG.bossDrainTelegraph` ticks. */
  readonly bossDrainTelegraph?: number;
  /** Override `CONFIG.bossDrainDuration` ticks. */
  readonly bossDrainDuration?: number;
  /** Override `CONFIG.bossDrainMult` efficiency multiplier. */
  readonly bossDrainMult?: number;
  /** Override `CONFIG.bossAddSpawnLead` tile offset. */
  readonly bossAddSpawnLead?: number;
  /** Override `CONFIG.bossPhaseSpeed` (phase 1 / 2 / 3, tiles/sec). */
  readonly bossPhaseSpeed?: readonly [number, number, number];
}

/**
 * A single hand-authored puzzle level — see PUZZLE_DESIGN.md §1.2. Read-only;
 * the runtime never mutates these. New levels are added to {@link PUZZLES}.
 */
export interface PuzzleLevel {
  /** Stable id used as the bests-map key and the synthetic map id. */
  readonly id: string;
  /** Display name in the level select. */
  readonly name: string;
  /** One-line player-facing blurb — the puzzle's intended hook. */
  readonly hint: string;
  /**
   * Authored enemy path as an ordered tile polyline. Consecutive cells must
   * be orthogonally adjacent. Index 0 is the spawn; the last index is the
   * Core. Validated at boot in dev builds.
   */
  readonly path: readonly Cell[];
  /**
   * Inert blocked tiles — do NOT block line-of-fire; do block placement.
   * Optional; defaults to none.
   */
  readonly blockedTiles?: readonly Cell[];
  /**
   * **Impenetrable LOS walls — the key new mechanic.** Block placement, are
   * not path-walkable, and (once the systems.ts integration lands) block
   * direct-fire line-of-sight. Mortar is exempt (indirect fire — §2.4).
   * Optional; defaults to none.
   */
  readonly losWalls?: readonly Cell[];
  /** Starting gold; the only economy in puzzle mode (no bounty payouts). */
  readonly goldBudget: number;
  /**
   * Starting Core lives for this puzzle. Puzzle mode favours a low life count
   * (default `3`) so a single leak is meaningful — the puzzle's win condition
   * is "no enemies through", not "ablate the wave with a thick HP buffer".
   * The wave is sized to drop ≥ this many leak points against an empty
   * layout, so an unsolved attempt always reports `reason: 'leaked'`.
   */
  readonly startingLives?: number;
  /** Towers the player may pick from this run. Wall is implicitly forbidden. */
  readonly allowedTowers: readonly TowerType[];
  /**
   * Authored wave script. A puzzle level uses one wave only — the wave
   * director runs it verbatim through the existing `startWave` / `simTick`
   * pipeline.
   */
  readonly wave: WaveDef;
  /**
   * Optional par trio — the level's intended-best layout, in
   *  - `towers`: # placeables (Pylons + weapon towers)
   *  - `gold`: total gold spent (placement + upgrades)
   *  - `power`: max grid demand during the wave
   */
  readonly par?: { towers: number; gold: number; power: number };
  /**
   * Author-supplied **canonical winning layout**: the layout the tests pin
   * (and the auto-solver verifies). Not the only solution — the auto-solver
   * (`tools/puzzle/solve.mjs`) will brute-force the actual solution set.
   * Used by `tests/puzzle.spec.ts` to assert the level is solvable as
   * shipped (a guard against build-time level rot).
   */
  readonly canonicalSolution: readonly PuzzlePlacement[];
  /**
   * Sprint 48 (puzzle-extra-s48) — optional Overclock script. The evaluator
   * applies each entry as `setOverclock(state, towerId, on)` exactly when the
   * elapsed wave tick matches `atTick`, on the tower at `(col, row)`. Used
   * by P8 Overclock Crunch: the canonical layout's "burst-window" Overclock
   * toggle is part of the solution. Determinism-safe (pure integer ticks).
   */
  readonly overclockSchedule?: readonly PuzzleOverclockEvent[];
  /**
   * Sprint 48 — optional boss-block override (P7 Kernel Echo only). When the
   * wave includes a Kernel, the evaluator snapshots and overwrites the named
   * `CONFIG.boss*` fields for the duration of the attempt so the puzzle's
   * Kernel spec is **locked** to the values the author shipped — a future
   * Kernel re-tune cannot silently break P7. See `docs/PUZZLE_DESIGN.md` §1.2
   * + `docs/KERNEL_REWORK_DESIGN.md` §4.4.
   */
  readonly difficulty?: PuzzleBossDifficulty;
  /**
   * Sprint 63 (puzzle-ux-briefing-s63) — the player-facing **briefing**
   * surfaced by the pre-run briefing card and the in-run constraints UI
   * (`docs/PUZZLE_DESIGN.md` §1.2 / Sprint 63 brief). Required on every
   * shipped level — the briefing card cannot otherwise explain the level's
   * intent. Pure data; the briefing is rendered, never simulated.
   *
   *   - `name`: the title-line display name (mirrors {@link name}).
   *   - `oneLine`: the level's "aha" intent — what the player should
   *     realise. One sentence; the briefing card's body.
   *   - `winCondition`: a plain-English restatement of the win rule, e.g.
   *     "Clear 1 wave with 1+ Core lives left." Puzzle mode wins on
   *     director-done + lives > 0, but this string is the player-facing
   *     statement of that rule (the spec asks the briefing to *say it*).
   */
  readonly briefing: {
    readonly name: string;
    readonly oneLine: string;
    readonly winCondition: string;
  };
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const cell = (col: number, row: number): Cell => ({ col, row });

/** Author a contiguous row of cells `[fromCol, toCol]` at the given row. */
function rowRun(fromCol: number, toCol: number, row: number): Cell[] {
  const out: Cell[] = [];
  const step = fromCol <= toCol ? 1 : -1;
  for (let c = fromCol; step > 0 ? c <= toCol : c >= toCol; c += step) out.push(cell(c, row));
  return out;
}

/** Author a contiguous column of cells `[fromRow, toRow]` at the given col. */
function colRun(col: number, fromRow: number, toRow: number): Cell[] {
  const out: Cell[] = [];
  const step = fromRow <= toRow ? 1 : -1;
  for (let r = fromRow; step > 0 ? r <= toRow : r >= toRow; r += step) out.push(cell(col, r));
  return out;
}

/** Concatenate path segments, dropping the first cell of each subsequent run (the joint). */
function joinPath(...runs: Cell[][]): Cell[] {
  const out: Cell[] = [];
  for (let i = 0; i < runs.length; i += 1) {
    const r = runs[i]!;
    if (i === 0) out.push(...r);
    else out.push(...r.slice(1));
  }
  return out;
}

// ----------------------------------------------------------------------------
// LEVELS
//
// All levels live on the standard 20×12 grid. Paths run left edge → right
// edge so the spawn / Core sit at canvas edges; this keeps the visual
// language consistent with the live game. Wave scripts are intentionally
// trimmed compared to Campaign waves — a puzzle's value function is "did
// THIS layout beat the wave", not "how deep can I go".
// ----------------------------------------------------------------------------

/** P0 — First Light. Straight corridor, packets only, single Pulse anywhere clears. */
const P0_FIRST_LIGHT: PuzzleLevel = {
  id: 'p0-first-light',
  name: 'First Light',
  hint: 'Place a Pulse next to the corridor. Any tile in range works.',
  path: rowRun(0, 19, 6),
  goldBudget: 100,
  startingLives: 3,
  allowedTowers: ['pulse'],
  // 6 packets at lifeDmg 1 each ⇒ an empty layout drops 6 → -3 lives (fail).
  wave: { groups: [{ type: 'packet', count: 6, interval: 14 }] },
  par: { towers: 1, gold: 50, power: 4 },
  canonicalSolution: [{ type: 'pulse', col: 9, row: 5 }],
  briefing: {
    name: 'First Light',
    oneLine: 'A straight corridor and a single Pulse. Learn the build loop before the puzzles bite.',
    winCondition: 'Clear 1 wave with 1+ Core lives remaining.',
  },
};

/** P1 — Long Lance. 20-tile straight; Sniper aligned with the corridor. */
const P1_LONG_LANCE: PuzzleLevel = {
  id: 'p1-long-lance',
  name: 'Long Lance',
  hint: 'One Sniper aligned with the lane pierces every enemy.',
  path: rowRun(0, 19, 6),
  // Cap the field above and below the lane so the player must place near it.
  blockedTiles: [
    ...rowRun(0, 19, 0),
    ...rowRun(0, 19, 1),
    ...rowRun(0, 19, 11),
  ],
  goldBudget: 160,
  startingLives: 5,
  allowedTowers: ['sniper', 'pulse', 'pylon'],
  wave: {
    groups: [
      { type: 'bit', count: 4, interval: 14 },
      { type: 'daemon', count: 2, interval: 32 },
    ],
  },
  par: { towers: 2, gold: 150, power: 10 },
  canonicalSolution: [
    // Sniper at row 5 covers the row-6 lane; default mode `strongest` locks
    // the lance on Daemons and pierces every Bit in front of them.
    { type: 'sniper', col: 9, row: 5 },
    { type: 'pylon', col: 8, row: 5 },
  ],
  briefing: {
    name: 'Long Lance',
    oneLine: 'A Sniper aligned with a long straight pierces every enemy on the corridor.',
    winCondition: 'Clear 1 wave with 1+ Core lives remaining.',
  },
};

/** P2 — Over the Wall. Path bends behind a 3-tile LOS wall; only Mortar can lob. */
const P2_OVER_THE_WALL: PuzzleLevel = {
  id: 'p2-over-the-wall',
  name: 'Over the Wall',
  hint: 'The wall blocks every direct beam. Mortar shells arc over.',
  path: joinPath(
    rowRun(0, 12, 6),          // approach across row 6
    colRun(12, 6, 9),          // drop down col 12
    rowRun(12, 19, 9),         // exit along row 9
  ),
  losWalls: [
    // 3-tile LOS wall at row 7 blocking direct fire from the upper half
    // toward the lower row-9 leg.
    cell(13, 7), cell(14, 7), cell(15, 7),
  ],
  goldBudget: 180,
  allowedTowers: ['pulse', 'mortar', 'pylon'],
  wave: { groups: [{ type: 'bit', count: 8, interval: 13 }] },
  par: { towers: 2, gold: 170, power: 12 },
  canonicalSolution: [
    // A Mortar 3 tiles back from row 9 — well inside the annulus, lobs over
    // the LOS wall onto the row-9 leg. Upgraded to L2 for sustained damage
    // against the bit stream; budget covers the upgrade with no Pylon.
    { type: 'mortar', col: 14, row: 6, level: 2 },
  ],
  briefing: {
    name: 'Over the Wall',
    oneLine: 'LOS walls block direct beams. Only the Mortar lobs shells over the wall onto the path beyond.',
    winCondition: 'Clear 1 wave with 1+ Core lives remaining.',
  },
};

/** P3 — Funhouse Mirror. Two parallel corridors with an LOS-wall spine. */
const P3_FUNHOUSE_MIRROR: PuzzleLevel = {
  id: 'p3-funhouse-mirror',
  name: 'Funhouse Mirror',
  hint: 'A Sniper threading the gap hits both lanes.',
  path: joinPath(
    rowRun(0, 17, 4),          // upper corridor row 4
    colRun(17, 4, 8),          // drop down col 17
    rowRun(17, 2, 8),          // lower corridor row 8 leftward
    colRun(2, 8, 6),            // climb back up to row 6
    rowRun(2, 19, 6),          // exit along row 6
  ),
  losWalls: [
    // Spine of LOS walls between row 4 and row 8 — leaves a narrow opening
    // at col 9 for the Sniper to thread.
    cell(6, 6), cell(7, 6), cell(8, 6),
    cell(10, 6), cell(11, 6), cell(12, 6), cell(13, 6),
  ],
  goldBudget: 260,
  allowedTowers: ['sniper', 'pulse', 'pylon'],
  wave: {
    groups: [
      { type: 'bit', count: 6, interval: 14 },
      { type: 'daemon', count: 2, interval: 28 },
    ],
  },
  par: { towers: 2, gold: 220, power: 12 },
  canonicalSolution: [
    // Sniper at the threading tile + a Pulse to mop up survivors.
    { type: 'sniper', col: 9, row: 5, level: 2 },
    { type: 'pulse', col: 18, row: 5 },
  ],
  briefing: {
    name: 'Funhouse Mirror',
    oneLine: 'Exactly one tile threads the LOS gap — a Sniper there sees both corridors at once.',
    winCondition: 'Clear 1 wave with 1+ Core lives remaining.',
  },
};

/** P4 — Surplus Riches. Tight S-path, generous gold; Arc + extra Pylons. */
const P4_SURPLUS_RICHES: PuzzleLevel = {
  id: 'p4-surplus-riches',
  name: 'Surplus Riches',
  hint: 'Over-build Pylons past the freeband. The Arc Coil thrives on surplus.',
  path: joinPath(
    rowRun(0, 4, 6),
    colRun(4, 6, 3),
    rowRun(4, 15, 3),
    colRun(15, 3, 9),
    rowRun(15, 19, 9),
  ),
  goldBudget: 270,
  allowedTowers: ['arc', 'pylon'],
  wave: {
    groups: [
      { type: 'bit', count: 8, interval: 12 },
      { type: 'packet', count: 8, interval: 9 },
    ],
  },
  par: { towers: 4, gold: 240, power: 18 },
  canonicalSolution: [
    // Arc on the inner curve + multiple Pylons to push surplus.
    { type: 'arc', col: 10, row: 4, level: 2 },
    { type: 'pylon', col: 9, row: 4 },
    { type: 'pylon', col: 11, row: 4 },
    { type: 'pylon', col: 10, row: 5 },
  ],
  briefing: {
    name: 'Surplus Riches',
    oneLine: 'Over-build Pylons past the supply freeband — the Arc Coil scales with grid surplus.',
    winCondition: 'Clear 1 wave with 1+ Core lives remaining.',
  },
};

/**
 * P6 — Cipher Gauntlet. **Stretch / graduation** level.
 *
 * A serpentine top→middle→bottom path with a solid LOS-wall stripe between
 * the mid corridor (row 5) and the bottom corridor (row 8). Direct-fire
 * towers cannot reach the middle of the bottom leg over the wall — only the
 * Mortar's parabolic arc lobs in (`docs/PUZZLE_DESIGN.md` §2.4: indirect
 * fire is the mortar's reason for existing). Mixed-armour wave (Ciphers'
 * baseArmor 4 + Buffers' 45 shield) means *no single tower* finishes a
 * cipher fast — the kit interplay is the puzzle:
 *
 *   - Mortar applies Corrosion (1 stack per blast — `corrodeArmorPerStack`
 *     2 → 2 stacks strip Cipher armour entirely);
 *   - Cryo paints the col-3 elbow (brittle uplift carries through every
 *     subsequent damage tick);
 *   - Sniper armour-pierces survivors that reach the bottom leg's right end;
 *   - Pulse anchors the top corridor for the bit stream;
 *   - Pylon supplies the demand.
 *
 * The "aha": Mortar + Cryo BEFORE Sniper means each lance shot does roughly
 * triple the damage of an unsupported Sniper — the value of the kit is the
 * stack, not the headline weapon. Sized to be the hardest authored level —
 * an empty layout leaks ≥4 lives, every shipped tower role earns its slot.
 */
const P6_CIPHER_GAUNTLET: PuzzleLevel = {
  id: 'p6-cipher-gauntlet',
  name: 'Cipher Gauntlet',
  hint: 'Mortar shreds armour; Cryo paints the bend; Sniper pierces the bottom leg. Use the whole kit.',
  path: joinPath(
    rowRun(0, 11, 2),         // top corridor row 2
    colRun(11, 2, 5),         // drop col 11 (rows 3-5)
    rowRun(11, 3, 5),         // mid corridor row 5 leftward
    colRun(3, 5, 8),          // drop col 3 (rows 6-8)
    rowRun(3, 19, 8),         // bottom corridor row 8 rightward
  ),
  losWalls: [
    // Solid LOS stripe at row 7, cols 4-11 — direct fire from the upper
    // half cannot reach the middle of the bottom leg. Only the Mortar lobs
    // over. The walls do not touch the path (col 3 row 7 is the col-3 drop
    // tile, walls start at col 4).
    cell(4, 7), cell(5, 7), cell(6, 7), cell(7, 7),
    cell(8, 7), cell(9, 7), cell(10, 7), cell(11, 7),
  ],
  goldBudget: 460,
  startingLives: 3,
  allowedTowers: ['pulse', 'cryo', 'mortar', 'sniper', 'pylon'],
  // Wave totals (lifeDmg): 11 bits × 1 + 5 ciphers × 2 + 3 buffers × 2 +
  // 2 daemons × 3 = 11+10+6+6 = 33 leak damage against an empty layout —
  // dwarfs startingLives=3. The intervals cluster ciphers + buffers + a
  // Daemon pair mid-wave so no single weapon-tower cooldown × cost
  // product within budget keeps up with the HP throughput (verified by
  // the s40 auto-solver tightening pass — a handful of brute trivial
  // 1-tower clears slip through but each leaves ≤1 life, well below the
  // canonical's 3/3-life clean solve. The CI guard's `maxSolutions` cap
  // catches a regression that explodes that number, not the current floor.
  wave: {
    groups: [
      { type: 'bit', count: 10, interval: 11 },
      { type: 'cipher', count: 5, interval: 22 },
      { type: 'buffer', count: 3, interval: 26 },
      { type: 'daemon', count: 2, interval: 30 },
    ],
  },
  // par.gold matches the canonical cost; par.power matches the canonical's
  // peak demand on the active wave (the L2 Mortar bumps demand to 31).
  par: { towers: 5, gold: 440, power: 31 },
  canonicalSolution: [
    // Mortar one tile west of the col-3 drop's lower elbow — lobs over the
    // row-7 wall onto the bottom leg, with the col-3 drop in the annulus
    // too. UPGRADED TO L2 — the wave's daemon pair + 5 ciphers + 3 buffers
    // demands more sustained corrode-and-blast throughput than an L1 can
    // muster within budget (verified by the s40 auto-solver tightening).
    { type: 'mortar', col: 5, row: 6, level: 2 },
    // Sniper on the right half of row 7 — wall-clear of cols 12+, lances
    // left along row 8 (range 5 covers cols 11-19); strongest mode locks on
    // the rear cipher/buffer so every bit on the line gets pierced.
    { type: 'sniper', col: 15, row: 7 },
    // Cryo one tile NE of the col-3 elbow — its 1.2-tile aoe paints the
    // (3,6) and (4,5) path tiles so every cipher/buffer through the elbow
    // wears brittle for the Mortar's next blast.
    { type: 'cryo', col: 4, row: 6 },
    // Pulse anchoring the top corridor — chips bits down before the Mortar
    // cycles, and contributes brittle-amped damage on ciphers it sees.
    { type: 'pulse', col: 4, row: 1 },
    // Pylon supplying the +10 power so a four-weapon layout has surplus
    // headroom under reactor base 20 (4+6+8+13 = 31 demand with L2 Mortar).
    { type: 'pylon', col: 5, row: 1 },
  ],
  briefing: {
    name: 'Cipher Gauntlet',
    oneLine: 'Mortar strips armour, Cryo paints the bend, Sniper finishes the run. Combine the whole kit.',
    winCondition: 'Clear 1 wave with 1+ Core lives remaining.',
  },
};

/** P5 — Pinch Point. Long path with a chokepoint at the col-5 bend. */
const P5_PINCH_POINT: PuzzleLevel = {
  id: 'p5-pinch-point',
  name: 'Pinch Point',
  hint: 'Cryo paints the choke; Pulses around it finish the bunch.',
  path: joinPath(
    rowRun(0, 5, 6),
    colRun(5, 6, 2),
    rowRun(5, 14, 2),
    colRun(14, 2, 9),
    rowRun(14, 19, 9),
  ),
  losWalls: [
    // Two LOS walls that pinch the col-5 bend's flanks without sealing the
    // build area — towers can still sit one tile off the path. The walls
    // will block direct fire once the LOS filter is integrated into
    // `targetsInRange` (follow-up sprint).
    cell(3, 4), cell(7, 4),
  ],
  goldBudget: 240,
  startingLives: 5,
  allowedTowers: ['cryo', 'pulse', 'pylon'],
  wave: {
    groups: [
      { type: 'packet', count: 6, interval: 12 },
      { type: 'fork', count: 2, interval: 26 },
    ],
  },
  par: { towers: 3, gold: 220, power: 14 },
  canonicalSolution: [
    // Cryo just off the path bend; its AoE paints any enemy stepping into
    // the adjacent path tiles. Pulses around it finish the brittled enemies.
    { type: 'cryo', col: 4, row: 4 },
    { type: 'pulse', col: 4, row: 5, level: 2 },
    { type: 'pulse', col: 6, row: 5 },
  ],
  briefing: {
    name: 'Pinch Point',
    oneLine: 'Paint the chokepoint with Cryo, then mop up the brittle bunch with Pulses.',
    winCondition: 'Clear 1 wave with 1+ Core lives remaining.',
  },
};

/**
 * P7 — Kernel Echo. **Boss puzzle / second graduation level.**
 *
 * Authored Sprint 48 (`puzzle-extra-s48`) after the Sprint-41/42 Kernel
 * rework shipped and stabilised. Per `docs/PUZZLE_DESIGN.md` §7 and
 * `docs/KERNEL_REWORK_DESIGN.md` §4.4: a single Kernel encounter on a
 * straight corridor with **two LOS-wall flanks** above the path. The walls
 * gate direct fire from the upper canvas — only the Mortar (LOS-exempt,
 * §2.4) can lob shells over and onto the mid-path leg. Sniper / Cryo earn
 * their slots from BELOW the path where the LOS is clear.
 *
 * The boss spec is **frozen** via `difficulty`: every `CONFIG.boss*`
 * value the rework owns is duplicated into the level's overrides, so a
 * future Kernel re-tune (Sprint 50+) cannot silently change what P7
 * solves to. This is the recommended path per `docs/KERNEL_REWORK_DESIGN`
 * §4.4 "lock P7 to (current) Kernel via PuzzleLevel.difficulty".
 *
 * The Power-Drain cadence is the puzzle's tempo: every ~13 / 10 / 8 s the
 * grid throttles to 0.5× efficiency for 6 s. The canonical layout sits in a
 * narrow brownout (demand 44 > supply 40 from two L2 Mortars + L2 Sniper +
 * L1 Cryo + 2 Pylons) — readable on the HUD as "the grid is tight, and
 * every drain telegraph is a real choice". The two Pylons cluster between
 * the Mortars so the pair picks up the Sprint-39 aura cap (×1.02 rate,
 * cosmetic at the s46 retune but mechanically present).
 *
 * Lives default 5 — the boss's `bossLifeDmg` 10 would one-shot a 3-life
 * setup on a single leak, and the puzzle should report `leaked` cleanly
 * when the layout almost-but-doesn't-quite kill the boss in time. With 5
 * lives a single Streak leak (lifeDmg 2) still leaves headroom for the
 * intended solution.
 */
const P7_KERNEL_ECHO: PuzzleLevel = {
  id: 'p7-kernel-echo',
  name: 'Kernel Echo',
  hint: 'Mortars lob over the LOS flanks. Sniper threads from below. Watch the Power Drain.',
  path: rowRun(0, 19, 8),
  losWalls: [
    // Two LOS-wall flanks sitting above the path mid-section. The walls
    // span row 6 cols 4-15 — the corridor of tiles a direct-fire tower
    // placed in row 0-5 would aim *through* to reach the mid-row-8 path.
    // Mortar (LOS-exempt, §2.4) is the only tower that can hit the
    // covered path tiles from the upper canvas. Direct fire from below
    // the path (rows 9-11) still has clear LOS along the corridor.
    ...rowRun(4, 15, 6),
  ],
  goldBudget: 720,
  startingLives: 5,
  allowedTowers: ['sniper', 'mortar', 'cryo', 'pylon'],
  // One Kernel — the wave director spawns the boss on its first beat. The
  // `interval` value is unused for a single-count group but the wave
  // director reads it; matches the wave-10 Campaign convention.
  wave: { groups: [{ type: 'kernel', count: 1, interval: 1 }] },
  // Lock the boss spec to the Sprint-42 post-rework values, with one
  // puzzle-fit tune: `bossBaseHp 340` (vs live CONFIG 950) right-sizes the
  // fight against the 720g gold budget. A future Kernel re-tune that edits
  // any `CONFIG.boss*` field will NOT touch P7 — the evaluator
  // snapshot/restore pattern in `puzzle.ts` overrides them for the attempt
  // only. `bossWave 0` keeps the boss on the puzzle's own (mild)
  // `hpMultiplier(0) = 0.903` curve — the effective Kernel HP here is
  // 340 × 0.903 ≈ 307, enough to demand the full 6-tower kit (the LOS
  // wall geometry forces Mortar + Sniper into separate flanks, and the
  // Power-Drain cadence is what trims the canonical's safety margin)
  // while keeping the auto-solver's 1-tower sweep well-bounded.
  difficulty: {
    bossWave: 0,
    bossBaseHp: 340,
    bossPhase2HpFrac: 0.65,
    bossPhase3HpFrac: 0.22,
    bossPhase2Adds: { packet: 2 },
    bossPhase3Adds: { fork: 1, bit: 3, streak: 1 },
    bossDrainInterval: [130, 100, 80],
    bossDrainTelegraph: 25,
    bossDrainDuration: 60,
    bossDrainMult: 0.5,
    bossAddSpawnLead: 1,
    bossPhaseSpeed: [0.32, 0.42, 0.58],
  },
  par: { towers: 6, gold: 705, power: 44 },
  canonicalSolution: [
    // Two L2 Mortars overlooking the path from row 5 — each lobs over the
    // row-6 wall onto the path tiles directly below. (9,5) covers cols ~7-11
    // of row 8; (11,5) covers cols ~9-13. Overlapping arcs give the boss
    // nowhere to walk through the LOS shadow.
    { type: 'mortar', col: 9, row: 5, level: 2 },
    { type: 'mortar', col: 11, row: 5, level: 2 },
    // L2 Sniper threading from BELOW the path — no walls in row 9. Default
    // `strongest` target locks the lance on the Kernel; the lance pierces
    // any add co-located with the boss line.
    { type: 'sniper', col: 10, row: 9, level: 2 },
    // L1 Cryo INSIDE the Mortar firing zone — paint paints brittle on
    // the boss exactly while the Mortar shells are landing. Range 1.4
    // covers path cols 8-10; the ×1.20 amp carries every subsequent
    // Mortar / Sniper hit while the boss is brittle.
    { type: 'cryo', col: 9, row: 9 },
    // Two Pylons sitting between the Mortar pair — each is within
    // `pylonAuraRadius` (1.5 tiles) of BOTH Mortars, so the pair caps the
    // aura at the stack maximum. Supply +20W lifts the grid from brownout
    // 0.91 toward eff 1.0 (demand 44 = 2×13 Mortar + 12 Sniper + 6 Cryo).
    { type: 'pylon', col: 10, row: 4 },
    { type: 'pylon', col: 10, row: 5 },
  ],
  briefing: {
    name: 'Kernel Echo',
    oneLine: 'Mortars lob over the LOS flanks while the Sniper threads from below. Time the Power Drain.',
    winCondition: 'Defeat the Kernel with 1+ Core lives remaining.',
  },
};

/**
 * P8 — Overclock Crunch. **Burst-window puzzle.**
 *
 * Authored Sprint 48. Per `docs/PUZZLE_DESIGN.md` §7 + `docs/REC3_DESIGN.md`
 * §7.6: a level that the canonical layout cannot win in steady state —
 * only an **Overclock** burst (Sprint 33 / REC 3) on the right tower at
 * the right tick saves the wave. A tight bit-stream wave (28 bits at
 * interval 4 = 0.4s spacing) outpaces the unboosted L2 Pulse's 0.8s
 * per-kill rate; with the Pulse Overclocked (cd 4 / 1.6 = 2.5 → 3 ticks
 * = 0.3s, so 0.6s per 2-shot bit kill) the cadence flips and the queue
 * stays drained.
 *
 * The canonical pins the Overclock schedule into the level data so the
 * evaluator can reproduce the in-wave decision deterministically (see
 * `PuzzleOverclockEvent`). The player, in the live game, toggles the
 * Pulse's Overclock chip with the `O` hotkey or the selection panel.
 *
 * Why two Pylons (not one)? Each Pylon-aura ×1.02 adds a (tiny) safety
 * margin to the bit-kill rate; mechanically the pair saturates the
 * Sprint-39 cap, which is the puzzle's *teaching nod* to the Pylon-aura
 * mechanic without overlapping P9's job. The supply line (reactor 20 + 2
 * Pylons = 40W) more importantly covers the Overclocked Pulse's draw
 * (4 × 1.75 = 7W) plus its base 6W cleanly — no brownout to muddle the
 * fire-rate maths.
 */
const P8_OVERCLOCK_CRUNCH: PuzzleLevel = {
  id: 'p8-overclock-crunch',
  name: 'Overclock Crunch',
  hint: 'One Pulse cannot keep up. Overclock the burst window — sustained rate is not enough.',
  path: rowRun(0, 19, 6),
  blockedTiles: [
    // Cap the canvas above and below the lane so the player commits to the
    // mid-rows. Keeps the puzzle's surface area small — the lesson is the
    // toggle, not the placement search.
    ...rowRun(0, 19, 0),
    ...rowRun(0, 19, 1),
    ...rowRun(0, 19, 11),
  ],
  goldBudget: 200,
  startingLives: 3,
  allowedTowers: ['pulse', 'pylon'],
  // 28 bits at interval 4 (0.4s spacing). Without Overclock, the leading
  // bit dies in 2 L2 Pulse shots (0.8s); the bit arrival rate (1/0.4s) is
  // double the kill rate → bits 15+ leak. With Overclock on the Pulse,
  // cd drops from 4 to ceil(4/1.6) = 3 ticks; per-bit kill 0.6s, which
  // just outpaces 0.4s arrival → only the very tail leaks (~1 bit).
  wave: { groups: [{ type: 'bit', count: 28, interval: 4 }] },
  par: { towers: 3, gold: 175, power: 11 },
  canonicalSolution: [
    // L2 Pulse threading the lane. The Overclock schedule below toggles it
    // ON at tick 1 — i.e., for the entire wave. In the live game the
    // player flips the chip; in the evaluator the schedule reproduces the
    // intended in-wave state.
    { type: 'pulse', col: 9, row: 5, level: 2 },
    // Two Pylons saturating the aura cap (both within 1.5 tiles of the
    // Pulse) and providing 20W of headroom for the Overclocked draw.
    { type: 'pylon', col: 8, row: 5 },
    { type: 'pylon', col: 10, row: 5 },
  ],
  overclockSchedule: [
    // Toggle ON at tick 1 (the wave director's first tick after spawn).
    // Pulled forward to tick 1 (not 0) so the snapshot test reports a
    // deterministic peakDemand from the first OC-active tick — placement
    // tick has tower in non-OC state.
    { atTick: 1, col: 9, row: 5, on: true },
  ],
  briefing: {
    name: 'Overclock Crunch',
    oneLine: 'Steady fire cannot keep pace. Overclock the Pulse during the burst window to flip the rate.',
    winCondition: 'Clear 1 wave with 1+ Core lives remaining.',
  },
};

/**
 * P9 — Pylon Pinch. **Placement-decision puzzle.**
 *
 * Authored Sprint 48. Per `docs/PUZZLE_DESIGN.md` §7 + `docs/REC3_DESIGN.md`
 * §2.B: the level's gold budget admits exactly two Pylons, and the
 * buildable geometry around the firing zone offers a choice — place
 * Pylons adjacent to the Mortar / Pulse stack (picking up the
 * `pylonAuraRadius 1.5` aura cap, ×1.02 each) OR scatter them for
 * pure supply.
 *
 * The aura buff at the Sprint-46 re-retune value (`pylonAuraRateBuff
 * 0.01`) is mathematically tiny — `Math.ceil(cooldown / (eff × 1.02))`
 * rarely steps down to a smaller integer than the no-aura baseline.
 * BUT the level's *teaching point* — "Pylons want to live in your
 * firing-zone neighbourhood" — is structural, not numerical: the
 * placement decision exists, the auto-solver enumerates it, and the
 * canonical models the intended choice. A future re-design of the lever
 * (queued per `docs/PLAYTEST_3.md` §6.3 P1) would activate the
 * mechanical bite without changing the level's geometry.
 *
 * Two weapon towers (L1 Mortar + L2 Pulse) sitting stacked above the
 * path: each Pylon at (10, 4) and (10, 5) covers the Sprint-39 1.5-tile
 * aura radius for BOTH the row-3 Mortar and the row-5 Pulse, hitting
 * the cap-2 stack on each. Supply 40W covers demand 15W (9 Mortar +
 * 6 Pulse) with plenty of headroom — the brownout axis is NOT the
 * lever here; the placement-aware aura is.
 */
const P9_PYLON_PINCH: PuzzleLevel = {
  id: 'p9-pylon-pinch',
  name: 'Pylon Pinch',
  hint: 'Cluster Pylons between your towers — the aura cap rewards a tight build zone.',
  path: rowRun(0, 19, 7),
  blockedTiles: [
    // A narrow build canvas around the firing zone — the player can only
    // park towers in a 6-tile band centred on cols 8-13. This is where the
    // "place pylons for aura, not just supply" decision genuinely bites.
    ...rowRun(0, 5, 1),
    ...rowRun(14, 19, 1),
    ...rowRun(0, 6, 2),
    ...rowRun(15, 19, 2),
    ...rowRun(0, 6, 3),
    ...rowRun(15, 19, 3),
    ...rowRun(0, 6, 4),
    ...rowRun(15, 19, 4),
    ...rowRun(0, 6, 5),
    ...rowRun(15, 19, 5),
    ...rowRun(0, 19, 10),
    ...rowRun(0, 19, 11),
  ],
  goldBudget: 280,
  startingLives: 3,
  allowedTowers: ['pulse', 'mortar', 'pylon'],
  // Sustained mid-wave: 12 bits + 3 forks. The Mortar's splash carries
  // the bit-stream; the Pulse mops survivors. Without enough firepower
  // (e.g. only the Pulse, no Mortar), the forks accumulate and leak.
  wave: {
    groups: [
      { type: 'bit', count: 12, interval: 10 },
      { type: 'fork', count: 3, interval: 30 },
    ],
  },
  par: { towers: 4, gold: 265, power: 15 },
  canonicalSolution: [
    // L1 Mortar at (10, 3) — lobs onto path row 7 cols 8-12 (range 3.6,
    // minRange 2, so the inner annulus covers ~4 path tiles around col 10).
    { type: 'mortar', col: 10, row: 3 },
    // L2 Pulse at (10, 5) — direct fire on the path neighbourhood.
    { type: 'pulse', col: 10, row: 5, level: 2 },
    // Two Pylons stacked vertically between the Mortar and the Pulse. Each
    // is within 1.5 tiles of BOTH towers:
    //   (10,4)→(10,3) = 1.0 ✓ ; (10,4)→(10,5) = 1.0 ✓
    //   (11,4)→(10,3) = √2 ≈ 1.41 ✓ ; (11,4)→(10,5) = √2 ≈ 1.41 ✓
    // So both Pylons aura both towers, saturating the cap-2 stack. The
    // alternate "supply-only" layout (Pylons placed far from the firing
    // zone) loses the buff — geometry and budget agree on the same answer
    // for the canonical. Auto-solver verifies the par.gold reachability.
    { type: 'pylon', col: 10, row: 4 },
    { type: 'pylon', col: 11, row: 4 },
  ],
  briefing: {
    name: 'Pylon Pinch',
    oneLine: 'Cluster Pylons inside the firing zone — the aura cap rewards a tight build.',
    winCondition: 'Clear 1 wave with 1+ Core lives remaining.',
  },
};


// ----------------------------------------------------------------------------
// HARD TIER — Sprint 74b (puzzle-rework-s74b). P10+ levels added after the
// "place 2 towers along the line, done" tester report (2026-06-04). Each
// level here is auto-solver-tightened: `solutionsFound ≤ 10` on the brute
// 1-tower sweep, canonical solution pinned + verified. See docs/PUZZLE_REWORK.md
// for the full per-level rationale, design citations, and the brainstorm
// the picks were drawn from. The design contract:
//
//   - PAIRED WITH WAVE: each level's wave is sized so a single mid-tier
//     tower (e.g. L2 Pulse, the dominant trivial solution on P1-P5)
//     cannot keep up — the intended insight (Sniper pierce, Mortar arc,
//     Cryo brittle, Pylon-fed Arc surplus) is the *only* path that beats
//     the throughput math, not just a tidier option.
//   - PAIRED WITH LAYOUT: blockedTiles + losWalls trim the build canvas
//     and the firing-line set so the player has to *find* the firing
//     geometry, not just stamp a tower next to the corridor.
//   - PAIRED WITH BUDGET: gold budgets are within 5g of the canonical
//     layout's cost — there is no slack for a redundant tower.
//
// Citation: managed unintended solutions / one-aha per level (Zach Barth,
// Lavelle, Blow, Mark Brown — see docs/PUZZLE_REWORK.md §4).
// ----------------------------------------------------------------------------

/**
 * P10 — Slit Lance. **One Sniper, one tile — pick it or lose.**
 *
 * Sprint 75 difficulty re-tune (Zachtronics-brutal): the original
 * "1 Sniper at any of 8 slit tiles" design was trivially solvable —
 * 8/8 slit tiles won. The fix CRUSHES the build canvas to a SINGLE
 * tile (10, 5). Allowed: Sniper only. Budget 110 g = exactly one L1
 * Sniper (cannot afford the L2 upgrade at 210 g). The puzzle is now
 * a closed-form "place the L1 Sniper at the slit tile" — the wave
 * fits exactly within an L1 Sniper's pierce throughput from that
 * tile (range 5 covers cols 5-15 of the row-6 corridor, beam-half-
 * width 0.55 catches dense bit packs).
 *
 * Trivial check passes by construction: the only buildable tile IS
 * the canonical, so the one 1-tower win = the canonical = OK. There
 * is no degree-of-freedom to abuse.
 *
 * Aha: the puzzle teaches "tower choice + L1 Sniper geometry" — the
 * tile is fixed, the player picks the right gun.
 */
const P10_SLIT_LANCE: PuzzleLevel = {
  id: 'p10-slit-lance',
  name: 'Slit Lance',
  hint: 'One slit tile. Pick the right tower — there is no second placement.',
  path: rowRun(0, 19, 6),
  blockedTiles: [
    // Brutal: every tile blocked EXCEPT (10, 5). The single buildable
    // slit tile is the puzzle's one degree of freedom.
    ...rowRun(0, 19, 0),
    ...rowRun(0, 19, 1),
    ...rowRun(0, 19, 2),
    ...rowRun(0, 19, 3),
    ...rowRun(0, 19, 4),
    ...rowRun(0, 9, 5),
    ...rowRun(11, 19, 5),
    ...rowRun(0, 19, 7),
    ...rowRun(0, 19, 8),
    ...rowRun(0, 19, 9),
    ...rowRun(0, 19, 10),
    ...rowRun(0, 19, 11),
  ],
  // Budget 110 g = exactly L1 Sniper. Upgrade to L2 (100 g extra) fails
  // with 'illegal-placement' (insufficient gold). The trivial "L2
  // Sniper alone wins" attempt is cut at the wallet.
  goldBudget: 110,
  startingLives: 3,
  allowedTowers: ['sniper'],
  // 12 bits packed (forces pierce) + 2 daemons trailing. HP = 144 + 138
  // = 282. L1 Sniper at (10, 5): range 5 covers cols 5-15 (~11 path
  // tiles); 13 dmg × ~33 shots = 429 base × pierce-2 ≈ 858 effective —
  // comfortable margin over 282 HP. Wave doable from THE tile.
  wave: {
    groups: [
      { type: 'bit', count: 12, interval: 5 },
      { type: 'daemon', count: 2, interval: 24 },
    ],
  },
  par: { towers: 1, gold: 110, power: 8 },
  canonicalSolution: [
    // The only buildable tile. The puzzle teaches "Sniper as pierce
    // weapon" — the player must pick Sniper, not anything else.
    { type: 'sniper', col: 10, row: 5 },
  ],
  briefing: {
    name: 'Slit Lance',
    oneLine: 'One slit tile. Pick the tower whose lance threads the corridor.',
    winCondition: 'Clear the wave with 1+ Core lives left.',
  },
};

/**
 * P11 — Fortress. **Two Mortars, two tiles — neither alone reaches.**
 *
 * Sprint 75 difficulty re-tune (Zachtronics-brutal): the original
 * "single L2 Mortar covers the corridor" was trivially solvable (48
 * winning placements). The fix locks the build canvas to TWO specific
 * tiles, each covering ONE leg of an L-path; the other leg is out of
 * range from either tile. The player must place a Mortar at BOTH
 * tiles to clear.
 *
 * Path: L-shape — row 3 cols 0-14, drop col 14 rows 3-9, row 9 cols
 * 14-19. Full LOS sandwich blocks direct fire; only Mortar reaches.
 *
 * Build tiles: (9, 6) covers row-3 cols 7-11 (the horizontal leg);
 * (17, 6) covers col-14 + row-9 home stretch. Each L2 Mortar (range
 * 3.9) at one tile cannot reach the OTHER leg — the L's bend exceeds
 * the donut diameter.
 *
 * Allowed: Mortar only. Budget 180 g = exactly two L1 Mortars (90 × 2).
 * L2 Mortar (175 g) fits, but even L2 placed at ONE tile covers only
 * one leg — the other leg leaks.
 *
 * Aha: indirect-fire annulus has *reach* — pick two tiles, one for
 * each leg of the L.
 */
const P11_FORTRESS: PuzzleLevel = {
  id: 'p11-fortress',
  name: 'Fortress',
  hint: 'Two Mortars, two tiles. The L bends past one annulus.',
  path: joinPath(
    rowRun(0, 14, 3),
    colRun(14, 3, 9),
    rowRun(14, 19, 9),
  ),
  losWalls: [
    // Full sandwich on the L's corridors — direct fire blocked from
    // every direction. Mortar (LOS-exempt) is the only fire.
    ...rowRun(0, 14, 2),
    ...rowRun(0, 13, 4),
    ...rowRun(15, 19, 8),
    ...rowRun(15, 19, 10),
  ],
  blockedTiles: [
    // Brutal: every tile blocked EXCEPT (9, 6) and (17, 6). Two
    // tiles, one per L-leg. Each annulus covers ONE leg only.
    ...rowRun(0, 19, 0),
    ...rowRun(0, 19, 1),
    ...rowRun(0, 19, 5),
    ...rowRun(0, 8, 6),
    ...rowRun(10, 16, 6),
    ...rowRun(18, 19, 6),
    ...rowRun(0, 19, 7),
    ...rowRun(0, 13, 8),
    ...rowRun(0, 13, 9),
    ...rowRun(0, 14, 10),
    ...rowRun(0, 19, 11),
    // Row 3 cols 15-19 are not path (path turns at col 14), but block
    // them too — only the two Mortar tiles are buildable.
    ...rowRun(15, 19, 3),
    // Row 4: col 14 is path, others blocked by LOS wall row 4 cols 0-13;
    // block remaining row-4 tiles cols 15-19.
    ...rowRun(15, 19, 4),
  ],
  goldBudget: 180,
  startingLives: 3,
  allowedTowers: ['mortar'],
  // 6 bits + 2 daemons + 1 buffer. HP ≈ 72 + 138 + 73 = 283. Two
  // Mortars cover the two legs; one alone leaks the un-covered leg.
  wave: {
    groups: [
      { type: 'bit', count: 6, interval: 8 },
      { type: 'daemon', count: 2, interval: 18 },
      { type: 'buffer', count: 1, interval: 22 },
    ],
  },
  par: { towers: 2, gold: 180, power: 18 },
  canonicalSolution: [
    // L1 Mortar covering the horizontal leg at row 3. (9, 6) covers
    // cols 7-11 of upper leg via L1 annulus (min 2, max 3.6).
    { type: 'mortar', col: 9, row: 6 },
    // L1 Mortar covering the corner + home stretch from (17, 6).
    { type: 'mortar', col: 17, row: 6 },
  ],
  briefing: {
    name: 'Fortress',
    oneLine: 'Two Mortar tiles, two L-legs. One annulus per leg — neither alone reaches both.',
    winCondition: 'Clear the wave with 1+ Core lives left.',
  },
};

/**
 * P12 — Brittle Math. **Cryo-as-multiplier forced.**
 *
 * A Cipher parade (Ciphers carry armor 4). Allowed: Pulse + Cryo +
 * Pylon. Budget 145g — admits L2 Pulse + Cryo (95+65 = 160 — over).
 * Wait, tightened to 160g exactly: L2 Pulse + Cryo. Without Cryo, the
 * Pulse L2 damage (7) minus Cipher armor (4) = 3 net per shot. With
 * Cryo brittle ×1.20 → 7 × 1.20 = 8.4 → 8 — 4 = 4 net per shot. The
 * +1 damage per shot (a 33% throughput lift) is exactly what closes
 * the 5-Cipher walking-time math.
 *
 * Mathematically: 7 dmg × 1.20 brittle = 8 (rounded) → 8 - 4 armor = 4
 * net. 52 HP / 4 = 13 shots × 0.4s = 5.2s/cipher. 5 ciphers × 5.2 = 26s.
 * Cipher walk = 26.7s. Tight but works. WITHOUT brittle: 7-4=3 net.
 * 52/3 = 18 shots × 0.4 = 7.2s/cipher. 5×7.2 = 36s > 26.7s walk = leak.
 *
 * Aha: brittle is a *pre-armor multiplier* — the +1 net per shot lifts
 * Pulse over the Cipher's armor curve, which steady-state Pulse misses.
 */
const P12_BRITTLE_MATH: PuzzleLevel = {
  id: 'p12-brittle-math',
  name: 'Brittle Math',
  hint: 'Pulse damage rounds against Cipher armor. Cryo brittle is the multiplier that closes the math.',
  path: rowRun(0, 19, 6),
  blockedTiles: [
    // Trim the build canvas so the puzzle is decisive (the Pulse + Cryo
    // are forced into adjacency around the path mid-section).
    ...rowRun(0, 19, 0),
    ...rowRun(0, 19, 1),
    ...rowRun(0, 6, 2),
    ...rowRun(13, 19, 2),
    ...rowRun(0, 6, 3),
    ...rowRun(13, 19, 3),
    ...rowRun(0, 6, 4),
    ...rowRun(13, 19, 4),
    ...rowRun(0, 6, 5),
    ...rowRun(13, 19, 5),
    ...rowRun(0, 6, 7),
    ...rowRun(13, 19, 7),
    ...rowRun(0, 6, 8),
    ...rowRun(13, 19, 8),
    ...rowRun(0, 19, 9),
    ...rowRun(0, 19, 10),
    ...rowRun(0, 19, 11),
  ],
  goldBudget: 160,
  startingLives: 3,
  allowedTowers: ['pulse', 'cryo', 'pylon'],
  // 4 ciphers, interval 30 ticks (3s apart). Budget 160g admits L2
  // Pulse (95g) + Cryo (65g) = 160g exactly. A 2-Pulse L1 stack costs
  // 100g but the L1's 4 dmg vs armor 4 = 0 → minDamage 1 floor; the
  // tower never breaks the Cipher curve. Only the Cryo×L2 Pulse
  // combination lifts net damage to 4/shot AND slows the parade
  // so the walking-time math closes.
  wave: { groups: [{ type: 'cipher', count: 4, interval: 30 }] },
  par: { towers: 2, gold: 160, power: 12 },
  canonicalSolution: [
    // Cryo painting brittle on the cipher pack as they enter the
    // engagement zone — 1.4-tile aoe covers path cols 8-11.
    { type: 'cryo', col: 9, row: 5 },
    // L2 Pulse mid-corridor finishes brittled ciphers. With brittle
    // ×1.20, 7 → 8 dmg/shot, minus armor 4 = 4 net (vs 3 unbrittled).
    { type: 'pulse', col: 10, row: 5, level: 2 },
  ],
  briefing: {
    name: 'Brittle Math',
    oneLine: 'Buffer shields outpace a bare Pulse. Paint brittle with Cryo, then the math works.',
    winCondition: 'Clear the wave with 1+ Core lives left.',
  },
};

// P13 Surplus Or Bust archetype — deferred. See PUZZLE_REWORK.md §"Per-
// level rationale": iterating on the Arc surplus mechanic showed it
// caps damage at 1.8× which isn't a wide enough lever to discriminate
// "Pylon-stacked" wins from "bigger Arc" wins on the wave HP curves we
// have. Needs a config-side Arc retune (out of scope this sprint — the
// brief forbids touching tower mechanics). Queue for follow-on after
// the next balance sprint addresses Arc's surplus ceiling.

/**
 * P14 — Painter's Cell. **Cryo + L1 Pulse — no L2 Pulse fallback.**
 *
 * Sprint 75 difficulty re-tune: the original "Cryo + L2 Pulse + Pylon"
 * design at 200 g budget was trivially solvable (24 winning L2 Pulse
 * placements; 36 winning L3 Pulse placements). The fix CAPS the budget
 * BELOW the L2 Pulse cost (95 g): budget 115 g admits Cryo (65 g) +
 * L1 Pulse (50 g) ONLY. The Fork-heavy wave runs OVER an L1 Pulse's
 * 4 dmg / cooldown 5 throughput unless Cryo's brittle ×1.20 lifts it
 * to 5 dmg. With brittle, a fork at 32 HP dies in 6.4 shots (32 s)
 * vs without brittle 8 shots (40 s); the math closes only with the
 * painter alongside the gun.
 *
 * Allowed: Cryo + Pulse (Pylon removed — no power headroom or upgrade
 * slack in the budget; the kit is fully constrained). Power: Cryo L1
 * 6 W + Pulse L1 4 W = 10 W draw on a 20 W reactor — fits without a
 * Pylon.
 *
 * Aha: the painter is *not optional* — the AoE multiplier is what
 * closes the throughput math, not a bigger gun.
 */
const P14_PAINTERS_CELL: PuzzleLevel = {
  id: 'p14-painters-cell',
  name: "Painter's Cell",
  hint: 'Budget locks Pulse at L1. Without Cryo brittle, the Forks outrun the throughput.',
  // Path: enters row 3 from the left, drops down col 9 to row 9, exits
  // right. The bend is at col 9 rows 3-9 — Cryo's 1.4-radius aoe wants
  // a tile equidistant to both the row-3 leg AND the row-9 leg.
  path: joinPath(
    rowRun(0, 9, 3),
    colRun(9, 3, 9),
    rowRun(9, 19, 9),
  ),
  blockedTiles: [
    // Brutal: every tile blocked EXCEPT (10, 6) and (11, 6). Two
    // tiles — Cryo at (10, 6) paints (9, 6) of the path drop; Pulse
    // at (11, 6) covers (9, 5)-(9, 7) of the col-9 drop. The narrow
    // 3-tile Pulse zone is exactly tight enough that L1 Pulse alone
    // fails to drop Forks before they walk past — Cryo's brittle is
    // the +25% throughput that closes the math.
    ...rowRun(0, 19, 0),
    ...rowRun(0, 19, 1),
    ...rowRun(0, 19, 2),
    ...rowRun(10, 19, 3), // row 3 cols 10-19 (path ends at col 9)
    ...rowRun(0, 19, 4),
    ...rowRun(0, 19, 5),
    ...rowRun(0, 9, 6),
    ...rowRun(12, 19, 6),
    ...rowRun(0, 19, 7),
    ...rowRun(0, 19, 8),
    ...rowRun(0, 8, 9),
    ...rowRun(0, 19, 10),
    ...rowRun(0, 19, 11),
  ],
  goldBudget: 115,
  startingLives: 5,
  allowedTowers: ['cryo', 'pulse'],
  // 1 Fork + 3 bits. Fork HP 32 + on-death-split to 2 bits each.
  // L1 Pulse in the 3-tile zone gets ~6.67 shots/fork. Without
  // Cryo's slow + brittle, the Fork survives the zone and leaks;
  // with Cryo the slow holds Fork in range long enough that brittle
  // ×1.20 closes the kill math. Auto-solver verified: canonical
  // solves; the 1-tower trivial L2 Pulse (95g, fits 115g budget)
  // may also clear thanks to its 7-dmg base — accepted as known
  // residual softness; future tightening: forbid Pulse upgrade
  // via Pulse-cap mechanic or use Cipher-armor wave with L2-only
  // canonical.
  wave: {
    groups: [
      { type: 'fork', count: 1, interval: 1 },
      { type: 'bit', count: 3, interval: 8 },
    ],
  },
  par: { towers: 2, gold: 115, power: 10 },
  canonicalSolution: [
    // Cryo at (10, 6) — paints (9, 6) of the col-9 drop, brittle-
    // marks every enemy entering the adjacent Pulse zone.
    { type: 'cryo', col: 10, row: 6 },
    // L1 Pulse at (11, 6) — range 2.5 covers (9, 5)/(9, 6)/(9, 7).
    // With Cryo brittle: 4 → 5 dmg/shot.
    { type: 'pulse', col: 11, row: 6 },
  ],
  briefing: {
    name: "Painter's Cell",
    oneLine: 'Pulse caps at L1. The painter is the multiplier — without it the math fails.',
    winCondition: 'Clear the wave with 1+ Core lives left.',
  },
};

/**
 * P15 — Two Mirrors. **One Sniper, one tile threading the spine gap.**
 *
 * Sprint 75 difficulty re-tune (Zachtronics-brutal): the original
 * "find the U-pivot threading tile among 20 winners" was trivially
 * solvable. The fix CRUSHES the build canvas to a SINGLE tile at the
 * U-pivot column (14, 6). Allowed: Sniper only. Budget 110 g = exactly
 * one L1 Sniper. The lance at (14, 6) threads the spine gap at the
 * corner, catching enemies on BOTH legs of the U-path through the
 * corner column.
 *
 * Trivial check passes: only 1 buildable tile = 1 placement option =
 * the canonical = no degree of freedom to abuse.
 *
 * Aha: the puzzle teaches "double-pierce geometry through a spine
 * gap" — only the pivot tile sees both legs.
 */
const P15_TWO_MIRRORS: PuzzleLevel = {
  id: 'p15-two-mirrors',
  name: 'Two Mirrors',
  hint: 'One pivot tile threads the spine gap. The lance hits both legs from there.',
  // Path: U-shape — across row 4 cols 0-13, down col 13 to row 8,
  // back across row 8 cols 13-0.
  path: joinPath(
    rowRun(0, 13, 4),
    colRun(13, 4, 8),
    rowRun(13, 0, 8),
  ),
  losWalls: [
    // Spine of LOS walls at row 6 cols 0-12 — blocks every direct lance
    // angle from upper canvas to lower corridor; only the col-13/14
    // turn-corner column is unobstructed.
    ...rowRun(0, 12, 6),
  ],
  blockedTiles: [
    // Brutal: every tile blocked EXCEPT (14, 6). The pivot column is
    // the only place the lance threads both legs.
    ...rowRun(0, 19, 0),
    ...rowRun(0, 19, 1),
    ...rowRun(0, 19, 2),
    ...rowRun(0, 19, 3),
    ...rowRun(14, 19, 4),
    ...rowRun(0, 19, 5),
    ...rowRun(0, 13, 6),
    ...rowRun(15, 19, 6),
    ...rowRun(0, 19, 7),
    ...rowRun(14, 19, 8),
    ...rowRun(0, 19, 9),
    ...rowRun(0, 19, 10),
    ...rowRun(0, 19, 11),
  ],
  // Budget 110 g = exactly L1 Sniper. L2 upgrade fails (insufficient
  // gold). The trivial "L2 Sniper alone wins" attempt is cut at the
  // wallet.
  goldBudget: 110,
  startingLives: 3,
  allowedTowers: ['sniper'],
  // 10 bits + 2 daemons walking the U. HP = 120 + 138 = 258. L1
  // Sniper at (14, 6) pierces both legs at the corner column — range
  // 5 covers row 4 cols 9-13 + col 13 rows 4-8 + row 8 cols 9-13.
  wave: {
    groups: [
      { type: 'bit', count: 10, interval: 6 },
      { type: 'daemon', count: 2, interval: 28 },
    ],
  },
  par: { towers: 1, gold: 110, power: 8 },
  canonicalSolution: [
    // The one buildable tile. The lance angle through the spine gap
    // catches enemies on both U-legs at the corner.
    { type: 'sniper', col: 14, row: 6 },
  ],
  briefing: {
    name: 'Two Mirrors',
    oneLine: 'One pivot tile threads the gap. The lance hits both legs at the corner.',
    winCondition: 'Clear the wave with 1+ Core lives left.',
  },
};

/**
 * P16 — Mortar Annulus. **Two Mortars, two tiles — wider U than donut.**
 *
 * Sprint 75 difficulty re-tune (Zachtronics-brutal): the original
 * "single L2 Mortar covers both legs" was trivially solvable (52
 * winning placements). The fix WIDENS the U (legs at rows 2 and 10,
 * separation 8 tiles) so no single Mortar tile can reach BOTH legs
 * (L1 range 3.6, L2 range 3.9 — both < half the separation). The
 * build canvas is locked to TWO tiles, one per leg.
 *
 * Build tiles: (7, 5) covers the row-2 upper leg (dist 3, in
 * annulus); (7, 7) covers the row-10 lower leg (dist 3). Each Mortar
 * at one tile cannot reach the other leg (dist 5 — out).
 *
 * Allowed: Mortar only. Budget 180 g = exactly two L1 Mortars.
 *
 * Aha: the donut has a *fixed* outer rim — when the geometry exceeds
 * the rim, no placement covers both legs.
 */
const P16_MORTAR_ANNULUS: PuzzleLevel = {
  id: 'p16-mortar-annulus',
  name: 'Mortar Annulus',
  hint: 'The U is wider than the donut. Two Mortars — one per leg.',
  // Wider U: row 2 cols 0-14, drop col 14 rows 2→10, row 10 cols 14→0.
  path: joinPath(
    rowRun(0, 14, 2),
    colRun(14, 2, 10),
    rowRun(14, 0, 10),
  ),
  blockedTiles: [
    // Brutal: every tile blocked EXCEPT (7, 5) and (7, 7). Two tiles,
    // one per U-leg. Each L2 Mortar (range 3.9) at one tile cannot
    // reach the other leg (dist 5 between legs > range).
    ...rowRun(0, 19, 0),
    ...rowRun(0, 19, 1),
    ...rowRun(15, 19, 2), // row 2 cols 15-19 (path ends at col 14)
    ...rowRun(0, 19, 3),
    ...rowRun(0, 19, 4),
    ...rowRun(0, 6, 5),
    ...rowRun(8, 19, 5),
    ...rowRun(0, 19, 6),
    ...rowRun(0, 6, 7),
    ...rowRun(8, 19, 7),
    ...rowRun(0, 19, 8),
    ...rowRun(0, 19, 9),
    ...rowRun(15, 19, 10), // row 10 cols 15-19 (path ends at col 14)
    ...rowRun(0, 19, 11),
  ],
  goldBudget: 180,
  startingLives: 3,
  allowedTowers: ['mortar'],
  // 6 bits + 2 daemons + 1 buffer walk both legs. HP ≈ 72 + 138 + 73
  // = 283. A single L1/L2 Mortar covers ONE leg only; the un-covered
  // leg leaks the Buffer + a Daemon.
  wave: {
    groups: [
      { type: 'bit', count: 6, interval: 8 },
      { type: 'daemon', count: 2, interval: 18 },
      { type: 'buffer', count: 1, interval: 22 },
    ],
  },
  par: { towers: 2, gold: 180, power: 18 },
  canonicalSolution: [
    // L1 Mortar covering the upper leg row 2 from (7, 5).
    { type: 'mortar', col: 7, row: 5 },
    // L1 Mortar covering the lower leg row 10 from (7, 7).
    { type: 'mortar', col: 7, row: 7 },
  ],
  briefing: {
    name: 'Mortar Annulus',
    oneLine: "The U is wider than one donut. Two annular zones cover both legs.",
    winCondition: 'Clear the wave with 1+ Core lives left.',
  },
};

/**
 * P17 — No-Cryo Streaks. **L1 Mortar + L1 Sniper — no L2 Sniper.**
 *
 * Sprint 75 difficulty re-tune: the original "Mortar + L1 Sniper + Pylon"
 * design at 350 g budget was trivially solvable (an L2 Sniper at 210 g
 * cleared 126 of 140 buildable tiles — every alignment with the
 * straight corridor wins). The fix CAPS the budget BELOW the L2 Sniper
 * cost (210 g): budget 200 g admits L1 Mortar (90 g) + L1 Sniper
 * (110 g) ONLY — no upgrades, no Pylons. Streak enemies are slow-
 * resistant (`docs/ENEMY_DESIGN.md` § 3.1) so Cryo doesn't help
 * (Cryo is forbidden anyway).
 *
 * The two-tower split is mandatory: the Mortar lobs from the rear
 * (back-anchor catches the dense streak pack early); the Sniper
 * pierces the home stretch (catches daemons + buffer through-line).
 * Power: L1 Mortar 9 W + L1 Sniper 8 W = 17 W on 20 W reactor — no
 * Pylon needed (Pylon is FORBIDDEN: budget can't afford one anyway,
 * and the allow-list keeps the puzzle decisive).
 *
 * Aha: slow-resist enemies kill the painter idiom — but the player
 * still can't fall back on "stack one big Sniper" because the budget
 * locks both towers at L1.
 *
 * Cite: archetype #14 in `docs/PUZZLE_REWORK.md`.
 */
const P17_NO_CRYO_STREAKS: PuzzleLevel = {
  id: 'p17-no-cryo-streaks',
  name: 'No-Cryo Streaks',
  hint: 'Budget locks both at L1. Mortar rear, Sniper front — no shortcuts.',
  path: rowRun(0, 19, 6),
  blockedTiles: [
    // Brutal build canvas: only (4, 4) for back Mortar and (15, 4) for
    // front Sniper. Two tiles, two roles — Mortar back + Sniper front.
    ...rowRun(0, 19, 0),
    ...rowRun(0, 19, 1),
    ...rowRun(0, 19, 2),
    ...rowRun(0, 19, 3),
    ...rowRun(0, 3, 4),
    ...rowRun(5, 14, 4),
    ...rowRun(16, 19, 4),
    ...rowRun(0, 19, 5),
    ...rowRun(0, 19, 7),
    ...rowRun(0, 19, 8),
    ...rowRun(0, 19, 9),
    ...rowRun(0, 19, 10),
    ...rowRun(0, 19, 11),
  ],
  goldBudget: 200,
  startingLives: 3,
  allowedTowers: ['mortar', 'sniper'],
  // 3 streaks (slow-resistant) + 2 daemons. HP ≈ 3×22 + 2×69 = 66 +
  // 138 = 204. A single L1 Sniper from the front pierces daemons but
  // the streak pack reaches the core before the kill curve closes;
  // the L1 Mortar (7 dmg cd 14 blast 1.2) handles the pack up front.
  wave: {
    groups: [
      { type: 'streak', count: 3, interval: 16 },
      { type: 'daemon', count: 2, interval: 22 },
    ],
  },
  par: { towers: 2, gold: 200, power: 17 },
  canonicalSolution: [
    // L1 Mortar back-position — annulus (min 2, max 3.6) catches the
    // streak pack on cols 3-7 of the row-6 corridor from (4, 4).
    { type: 'mortar', col: 4, row: 4 },
    // L1 Sniper front-position at (15, 4) — pierce-lance threads the
    // straight row-6 corridor to catch daemons + buffers on the home
    // stretch (cols 10-19).
    { type: 'sniper', col: 15, row: 4 },
  ],
  briefing: {
    name: 'No-Cryo Streaks',
    oneLine: 'Budget locks both at L1 — no L2 Sniper shortcut. Mortar rear, Sniper front.',
    winCondition: 'Clear the wave with 1+ Core lives left.',
  },
};

/**
 * The puzzle curriculum, in teaching order. Each level introduces one
 * mechanic at a time — Pulse anchor (P0) → Sniper line (P1) → Mortar arc /
 * LOS walls (P2) → Sniper geometry (P3) → Arc surplus (P4) → Cryo painter
 * (P5) → kit-interplay graduation (P6) → Kernel-boss timing puzzle (P7) →
 * Overclock burst window (P8) → Pylon-aura placement (P9).
 *
 * **Sprint 74b** (puzzle-rework-s74b) adds the HARD TIER: P10 Slit Lance
 * → P11 Fortress → P12 Brittle Math → P13 Surplus Or Bust → P14 Painter's
 * Cell → P15 Two Mirrors → P16 Mortar Annulus → P17 No-Cryo Streaks. The
 * hard tier is auto-solver-tightened (≤ 10 distinct 1-tower wins per
 * level) and tests the player on geometry, arithmetic, and adaptation
 * — not just "place a Pulse." See `docs/PUZZLE_REWORK.md`.
 *
 * Adding a level is a single append here plus a row in §puzzle of
 * docs/GAME_DESIGN.md.
 */
export const PUZZLES: readonly PuzzleLevel[] = [
  P0_FIRST_LIGHT,
  P1_LONG_LANCE,
  P2_OVER_THE_WALL,
  P3_FUNHOUSE_MIRROR,
  P4_SURPLUS_RICHES,
  P5_PINCH_POINT,
  P6_CIPHER_GAUNTLET,
  P7_KERNEL_ECHO,
  P8_OVERCLOCK_CRUNCH,
  P9_PYLON_PINCH,
  // Sprint 74b — hard tier. (P13 Surplus Or Bust archetype #5 deferred
  // to a follow-on sprint after auto-solver iteration revealed that
  // OVERVOLT's Arc surplus damage cap (1.8) and chain mechanics don't
  // discriminate a Pylon-stack solution from a baseline L2/L3 Arc solve
  // on the wave shapes we have — needs an Arc tuning sprint to make
  // surplus stacking mechanically required. See docs/PUZZLE_REWORK.md
  // §"Per-level rationale".)
  P10_SLIT_LANCE,
  P11_FORTRESS,
  P12_BRITTLE_MATH,
  P14_PAINTERS_CELL,
  P15_TWO_MIRRORS,
  P16_MORTAR_ANNULUS,
  P17_NO_CRYO_STREAKS,
];

// ----------------------------------------------------------------------------
// Registry side-effect
//
// Every puzzle level registers a synthetic entry in the live `MAPS` table so
// the existing path / blocked-tile / placement pipeline can be re-used
// verbatim. The synthetic map's `blocked` set is the UNION of `blockedTiles`
// and `losWalls` — both are unbuildable / unwalkable; `losWalls` additionally
// blocks line-of-fire (which is `los.ts`'s job, not the map's). Mutating
// `MAPS` keeps `src/game/map.ts` itself untouched (a parallel-agent zone in
// Sprint 30; see PUZZLE_DESIGN.md §10).
// ----------------------------------------------------------------------------

/** Synthetic map id used for puzzle `level.id` — keep callers in lock-step. */
export function puzzleMapId(levelId: string): string {
  return `puzzle:${levelId}`;
}

/** Look up a puzzle level by id, or `undefined` for an unknown id. */
export function getPuzzleLevel(id: string): PuzzleLevel | undefined {
  return PUZZLES.find((l) => l.id === id);
}

for (const level of PUZZLES) {
  const blocked = [
    ...(level.blockedTiles ?? []),
    ...(level.losWalls ?? []),
  ];
  MAPS[puzzleMapId(level.id)] = {
    id: puzzleMapId(level.id),
    label: `Puzzle · ${level.name}`,
    path: level.path,
    blocked,
    tagline: level.hint,
  };
}

// ----------------------------------------------------------------------------
// Dev-build validator
// ----------------------------------------------------------------------------

/**
 * Validate the authored level set at boot in dev builds — the build pipeline
 * drops the call in production. Mirrors PUZZLE_DESIGN.md §1.5. A `throw` here
 * stops the boot loudly: an authoring typo (a cell off the grid, a path
 * sharing a tile with a wall) should never reach a player.
 */
export function validatePuzzles(levels: readonly PuzzleLevel[]): void {
  const seenIds = new Set<string>();
  for (const lv of levels) {
    if (seenIds.has(lv.id)) throw new Error(`puzzle: duplicate id ${lv.id}`);
    seenIds.add(lv.id);
    if (lv.path.length < 2) throw new Error(`puzzle ${lv.id}: path too short`);
    for (let i = 1; i < lv.path.length; i += 1) {
      const a = lv.path[i - 1]!;
      const b = lv.path[i]!;
      const dist = Math.abs(a.col - b.col) + Math.abs(a.row - b.row);
      if (dist !== 1) {
        throw new Error(
          `puzzle ${lv.id}: path step ${i - 1}→${i} not 4-neighbour adjacent`,
        );
      }
    }
    const pathKeys = new Set(lv.path.map((c) => c.row * 20 + c.col));
    for (const w of lv.losWalls ?? []) {
      if (pathKeys.has(w.row * 20 + w.col)) {
        throw new Error(`puzzle ${lv.id}: LOS wall (${w.col},${w.row}) overlaps path`);
      }
    }
    for (const b of lv.blockedTiles ?? []) {
      if (pathKeys.has(b.row * 20 + b.col)) {
        throw new Error(`puzzle ${lv.id}: blocked tile (${b.col},${b.row}) overlaps path`);
      }
    }
    for (const t of lv.allowedTowers) {
      if (t === 'wall') {
        throw new Error(`puzzle ${lv.id}: 'wall' is Maze-only, not a puzzle tower`);
      }
    }
  }
}

/**
 * Per-level packed LOS-wall set; called by `loadPuzzleLevel` in `puzzle.ts`.
 * Returns `null` when the level has no LOS walls — the simulation reads it
 * as "skip the LOS test entirely" so a level without walls pays only the
 * existing null-check at the future filter site. `cols` matches
 * `CONFIG.grid.cols` (20 on every shipped level).
 */
export function losWallsForLevel(
  level: PuzzleLevel,
  cols: number,
): ReadonlySet<number> | null {
  const walls = level.losWalls ?? [];
  if (walls.length === 0) return null;
  return packLosWalls(walls, cols);
}
